import { BaseSyntheticEvent, useCallback, useMemo, useState } from "react";
import { Loader } from "../../components/ui/Loader";
import { DataTable } from "primereact/datatable";
import { TableHeader } from "../../components/ui/table-header";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { useToast } from "../../components/ui/toast-context-provider";
import { useQueryClient } from "react-query";
import { FilterMatchMode } from "primereact/api";
import { CustomModal, CustomModalProps } from "../../components/ui/MobileModal/custom-modal";
import { HorseGroup } from "../../queries/models/horse-group.model";
import { useAddHorseGroupMutation, useDeleteHorseGroupMutation, useEditHorseGroupMutation, useHorseGroupsQuery } from "../../queries/horse-groups.query";
import { SizeTypeForm } from "../SizeTypes/SizeTypesForm";

export function HorseGroupsPage() {
  const queryClient = useQueryClient();
  const horseGroupsQuery = useHorseGroupsQuery();
  const addHorseGroupMutation = useAddHorseGroupMutation();
  const editHorseGroupMutation = useEditHorseGroupMutation();
  const deleteHorseGroupMutation = useDeleteHorseGroupMutation();
  const toast = useToast();
  const [selected, setSelected] = useState<HorseGroup>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState<boolean>(false);
  const [globalFilterValue, setGlobalFilterValue] = useState("");

  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const handleGlobalFilterChange = useCallback((e: BaseSyntheticEvent) => {
    const value = e.target.value;
    let _filters = filters;
    _filters.global.value = value;
    setFilters(_filters);
    setGlobalFilterValue(value);
  }, [filters]);

  const handleAddNew = () => {
    setSelected({ id: 0, name: "", comment: "", order: 0 } as HorseGroup);
    setIsModalOpen(true);
  };

  const handleSave = (data: HorseGroup) => {
    return data?.id ? editExisting(data) : saveNew(data);
  };

  const saveNew = (data: HorseGroup) => {
    setIsSubmitting(true);
    const mutateOptions = {
      onSuccess: async () => {
        setSelected(undefined);
        toast.current?.show({ severity: "success", detail: "Zapisano!" });
        await queryClient.invalidateQueries();
      },
      onError: async (error: any) => {
        toast.current?.show({ severity: "error", detail: error.message });
      },
      onSettled: () => {
        setIsSubmitting(false);
        setIsModalOpen(false);
      },
    };

    const request: HorseGroup = {
      id: data.id,
      name: data.name,
      comment: data.comment,
      order: data.order,
    };

    return addHorseGroupMutation.mutateAsync(request, mutateOptions);
  };

  const editExisting = (data: HorseGroup) => {
    setIsSubmitting(true);
    const mutateOptions = {
      onSuccess: async () => {
        setSelected(undefined);
        toast.current?.show({ severity: "success", detail: "Zapisano!" });
        await queryClient.invalidateQueries();
      },
      onError: async (error: any) => {
        toast.current?.show({ severity: "error", detail: error.message });
      },
      onSettled: () => {
        setIsSubmitting(false);
        setIsModalOpen(false);
      },
    };

    const request: HorseGroup = {
      id: data.id,
      name: data.name,
      comment: data.comment,
      order: data.order,
    };

    return editHorseGroupMutation.mutateAsync(request, mutateOptions);
  };

  const mobileModalProps: CustomModalProps = {
    header: selected?.id ? selected.name : "Dodaj grupę koni",
    onClose: () => { setSelected(undefined); setIsModalOpen(false); },
    isOpen: isModalOpen,
    body: (
      <SizeTypeForm
        sizeType={{ id: selected?.id ?? 0, name: selected?.name ?? "", comment: selected?.comment ?? "", order: selected?.order ?? 0 }}
        onSave={(vals: any) => handleSave({ id: selected?.id ?? 0, name: vals.name, comment: vals.comment, order: vals.order } as HorseGroup)}
        onCancel={() => { setSelected(undefined); setIsModalOpen(false); }}
        onDelete={() => setIsDeleteConfirmationModalOpen(true)}
        isSubmitting={isSubmitting}
      />
    ),
  };

  const handleDelete = useCallback(() => {
    if (selected) {
      deleteHorseGroupMutation.mutateAsync(selected.id, {
        onSuccess: async () => {
          await queryClient.invalidateQueries();
          toast.current?.show({ severity: "success", detail: "Zapisano!" });
          setSelected(undefined);
          setIsDeleteConfirmationModalOpen(false);
          setIsModalOpen(false);
        },
        onError: async (error: any) => {
          toast.current?.show({ severity: "error", detail: error.message });
        },
      });
    }
  }, [deleteHorseGroupMutation, queryClient, selected, toast]);

  const confirmationModalProps: CustomModalProps = {
    header: "Potwierdź",
    body: "Czy chcesz usunąć?",
    height: "160px",
    width: "50%",
    isOpen: isDeleteConfirmationModalOpen,
    confirmation: true,
    onClose: () => setIsDeleteConfirmationModalOpen(false),
    centered: true,
    justified: true,
    onConfirm: handleDelete,
  };

  return (
    <>
      {horseGroupsQuery.isLoading ? (
        <div className="h-full flex align-items-center">
          <Loader type="spinner" />
        </div>
      ) : (
        <div className="h-full">
          {!!mobileModalProps && (<CustomModal {...mobileModalProps} />)}
          {!!confirmationModalProps && (<CustomModal {...confirmationModalProps} />)}
          <div className="p-1 ">
            <InputText className="w-full" placeholder="Filtr globalny" value={globalFilterValue} onChange={handleGlobalFilterChange} />
          </div>
          <div style={{ height: "calc(100% - 3rem)" }}>
            <DataTable
              value={horseGroupsQuery.data}
              resizableColumns
              scrollable
              scrollHeight="flex"
              selectionMode="single"
              selection={selected}
              onSelectionChange={(e) => { setSelected(e.value as HorseGroup); setIsModalOpen(true); }}
              filters={filters}
              globalFilterFields={["name", "comment"]}
              header={<TableHeader header="Grupy koni" showAddButton onClickAdd={() => { handleAddNew(); }} />}
            >
              <Column field="order" header="Kolejność" sortable />
              <Column field="name" header="Nazwa" sortable />
              <Column field="comment" header="Komentarz" sortable body={(x: any) => (<span style={{ whiteSpace: "break-spaces" }}>{x.comment}</span>)} />
            </DataTable>
          </div>
        </div>
      )}
    </>
  );
}
